import { deepEquals } from 'app/infrastructure/js';
import { toJSON } from 'app/infrastructure/platform';
import { ComponentContext } from '../types-uischema';

export const cache: any = {};

const getCacheKey = (entityContext: ComponentContext): string => {
  const [pk] = (entityContext && entityContext.path) || [undefined];
  return toJSON({ pk, appId: entityContext && entityContext.applicationId, mode: entityContext && entityContext.mode });
};

const getSubCacheKey = (entityContext: ComponentContext): string => {
  const [, ...rest] = (entityContext && entityContext.path) || [undefined];
  return toJSON(rest);
};

export const getFromCache = (entityContext: ComponentContext, id: number) => {

  const cacheKey = getCacheKey(entityContext);
  const subCacheKey = getSubCacheKey(entityContext);

  if (!cache[cacheKey] || !cache[cacheKey][subCacheKey]) {
    return undefined;
  }

  return cache[cacheKey][subCacheKey][id];
};

/**
 * Adding a components props to cache on it's first render, future mapStateToProps invocations may then
 * use getMemoized
 * 
 * @param entityContext of the current rendered entity
 * @param id Domain id
 * @param props props to cache
 */
export const memoize = (entityContext: ComponentContext, id: number, props: any) => {

  if (!entityContext || !entityContext.path || !entityContext.path.length) {
    return;
  }

  const cacheKey = getCacheKey(entityContext);
  const subCachekey = getSubCacheKey(entityContext);

  // Clear other entities cache
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  Object.keys(cache).forEach(key => key !== cacheKey && delete cache[key]);

  cache[cacheKey] = cache[cacheKey] || {};
  cache[cacheKey][subCachekey] = cache[cacheKey][subCachekey] || {};
  cache[cacheKey][subCachekey][id] = props;
};

/**
 * For performance reasons this caches functions and arrays, scenarios that react-redux shallow equality cannot handle.
 * In addition a custom deepEquals for the value properties and options is performed to determine 
 * if cached *props* should be returned or not.
 *
 *  If any of properties are modified since last render (using deep equality check), the cache will **not** be used
 * 
 * @param entityContext 
 * @param id 
 * @param props 
 */
export const getMemoized = (entityContext: ComponentContext, id: number, props: any) => {

  const cached = getFromCache(entityContext, id);

  if (!cached) {
    return undefined;
  }

  const changedProps = cached && Object.keys(props).find(key => !deepEquals(props[key], cached[key]));

  if (props.hostProps !== cached.hostProps) {
    // If entity-view has been recreated, hostProps will be a new function referring to a completely new `this`!
    // We cannot reuse the cached props in this case.
    return undefined;
  }

  const isValid = changedProps === undefined;

  return isValid ? cached : undefined;
};
